import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import PurchaseOrderProductSelector from "../../components/CustomComponents/modal/PurchaseOrderProductSelector";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import PurchaseOrderTabBar from "./purchaseOrderTabBar";
import swal from "sweetalert";

const UpdatePurchaseOrderItems = () => {
  const { poId } = useParams();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [poData, setPoData] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showAddProductsModal, setShowAddProductsModal] = useState(false);
  
  // Cache for unit conversion factors to avoid repeated API calls
  const unitConversionCache = useRef(new Map());

  // Fetch purchase order data
  const fetchPurchaseOrder = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);
      if (response.data && response.data.data) {
        setPoData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching purchase order:", error);
    }
  }, [poId, compID]);

  // Fetch purchase order items
  const fetchPurchaseOrderItems = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_PRODUCTS}?poId=${poId}&compId=${compID}`);
      const data = response.data.data || response.data || [];

      // Transform data to match component expectations
      const transformedData = Array.isArray(data) ? data.map(item => ({
        id: item.id || item.ID,
        menuItem_ID: item.menuItem_ID || item.MenuItem_ID,
        menuItem_Name: item.menuItem_Name || item.MenuItem_Name,
        menuItem_DefaultVariantTitle: item.menuItem_DefaultVariantTitle || item.MenuItem_DefaultVariantTitle,
        menuItem_Image: item.menuItem_Image,
        orderQuantity: item.orderQuantity?.toString() || item.OrderQuantity?.toString() || "",
        unit: item.unit || item.Unit || "",
        supplierPrice: item.supplierPrice?.toString() || item.SupplierPrice?.toString() || "",
        unitCost: item.unitCost?.toString() || item.UnitCost?.toString() || "",
        containerType: item.containerType || item.ContainerType || item.menuItem_PurchaseContainerType || item.MenuItem_PurchaseContainerType || "Individual",
        containerQuantity: item.containerQuantity?.toString() || item.ContainerQuantity?.toString() || item.menuItem_PurchaseContainerQty?.toString() || item.MenuItem_PurchaseContainerQty?.toString() || "1",
        menuItem_PurchaseContainerType: item.menuItem_PurchaseContainerType || item.MenuItem_PurchaseContainerType || item.containerType || item.ContainerType || "Individual",
        menuItem_PurchaseContainerQty: item.menuItem_PurchaseContainerQty || item.MenuItem_PurchaseContainerQty || item.containerQuantity || item.ContainerQuantity || 1,
        menuItem_MeasurementType: item.menuItem_MeasurementType || item.MenuItem_MeasurementType || item.MeasuringType || item.measuringType || "Unit",
        menuItem_DefaultSize: item.menuItem_DefaultSize || item.MenuItem_DefaultSize,
        menuItem_DefaultSizeUnit: item.menuItem_DefaultSizeUnit || item.MenuItem_DefaultSizeUnit,
        MeasuringType: item.MeasuringType || item.measuringType || item.menuItem_MeasurementType || item.MenuItem_MeasurementType || "Unit",
        totalSupplierPrice: item.totalSupplierPrice || item.TotalSupplierPrice || 0,
        totalCost: item.totalCost || item.TotalCost || 0,
        current_Quantity: item.current_Quantity ?? item.Current_Quantity ?? 0,
      })) : [];
      setPoItems(transformedData);
    } catch (error) {
      console.error("Error fetching PO items:", error);
      showErrorToast("Failed to load purchase order items");
      setPoItems([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [poId, compID]);

  // Fetch all products for selection (including archived).
  // Every branch has its own POs: when the PO owner is a branch, show that branch's stock in the modal.
  // When PO has a supplier, pass SupplierID so API returns supplier price from tbl_SupplierProductRelation.
  const fetchAllProducts = useCallback(async () => {
    if (!compID) return;

    try {
      const params = { CompID: compID };
      const userIsBranch = parentCompId && parseInt(parentCompId, 10) > 0;
      // PO owner is always current comp when viewing (each branch has his own purchase order)
      const poOwnerIsBranch = userIsBranch;

      if (poOwnerIsBranch) {
        params.BranchCompID = compID;
        params.ParentCompID = parseInt(parentCompId, 10);
      }

      const supplierID = poData?.supplierID ?? poData?.SupplierID;
      if (supplierID != null && supplierID > 0) {
        params.SupplierID = supplierID;
      }

      const response = await axiosInstance.get(APIs.GET_ALL_PRODUCTS_FOR_PURCHASE_ORDER, { params });
      const data = response.data?.data || response.data?.Data || response.data || [];
      setAllProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load products:", error);
      setAllProducts([]);
    }
  }, [compID, parentCompId, poData]);

  // Handle product selection changes (memoized to prevent unnecessary re-renders)
  const handleProductSelectionChange = useCallback((selectedProducts) => {
    setPoItems(selectedProducts);
  }, []);

  // Calculate total supplier price from all items (using state instead of API)
  const calculateTotalSupplierPrice = useCallback(() => {
    if (!poItems || poItems.length === 0) return 0;
    
    const total = poItems.reduce((sum, item) => {
      const orderQty = parseFloat(item.orderQuantity || 0);
      const supplierPrice = parseFloat(item.supplierPrice || 0);
      return sum + (orderQty * supplierPrice);
    }, 0);

    return total;
  }, [poItems]);

  // Calculate total quantity from all items (using state instead of API)
  const calculateTotalQuantity = useCallback(() => {
    if (!poItems || poItems.length === 0) return 0;

    const totalQty = poItems.reduce((sum, item) => {
      const orderQty = parseFloat(item.orderQuantity || 0);
      return sum + orderQty;
    }, 0);

    return totalQty;
  }, [poItems]);

  // Get unit conversion factor from API (how many of this unit = 1 base unit)
  // Uses cache to avoid repeated API calls for the same unit
  const getUnitConversionFactor = useCallback(async (unitName) => {
    if (!unitName || unitName.toLowerCase() === "units" || unitName.toLowerCase() === "unit") {
      return 1;
    }

    const normalizedUnitName = unitName.trim().toLowerCase();
    
    // Check cache first
    if (unitConversionCache.current.has(normalizedUnitName)) {
      return unitConversionCache.current.get(normalizedUnitName);
    }

    try {
      const response = await axiosInstance.get(APIs.GET_UNIT_BY_NAME, {
        params: { unitName: unitName.trim() }
      });

      if (response.data && response.data.status && response.data.data) {
        const unit = response.data.data;
        const conversionFactor = unit.Measurement || 1;
        // Cache the result
        unitConversionCache.current.set(normalizedUnitName, conversionFactor);
        return conversionFactor;
      }
    } catch (error) {
      console.warn(`Failed to get conversion factor for unit "${unitName}":`, error);
    }

    // Fallback to common conversions if API fails
    const unitConversions = {
      'gram': 0.001, 'grams': 0.001, 'g': 0.001,
      'kilogram': 1, 'kilograms': 1, 'kg': 1,
      'milliliter': 0.001, 'milliliters': 0.001, 'ml': 0.001,
      'liter': 1, 'liters': 1, 'l': 1,
    };

    const conversionFactor = unitConversions[normalizedUnitName] || 1;
    // Cache the fallback result too
    unitConversionCache.current.set(normalizedUnitName, conversionFactor);
    return conversionFactor;
  }, []);

  // Calculate base unit quantity for an item
  const calculateBaseUnitQuantity = useCallback(async (item) => {
    const orderQuantity = parseFloat(item.orderQuantity || item.OrderQuantity || 0);
    const containerQuantity = parseFloat(item.containerQuantity || item.ContainerQuantity || 1);
    const containerType = item.containerType || item.ContainerType || "Individual";
    const unit = item.unit || item.Unit || "";

    // Calculate total quantity in purchased units
    const totalQuantity = (containerType.toLowerCase() === "individual" || containerQuantity <= 1)
      ? orderQuantity
      : orderQuantity * containerQuantity;

    // Get conversion factor (Measurement represents how much of the base unit each unit equals)
    const conversionFactor = await getUnitConversionFactor(unit);

    // Convert to base unit: total quantity * conversion factor
    // Example: Gram has Measurement = 0.001, so 1000 grams * 0.001 = 1 kg
    const baseUnitQuantity = conversionFactor > 0 ? totalQuantity * conversionFactor : totalQuantity;

    return baseUnitQuantity.toString();
  }, [getUnitConversionFactor]);

  // Distribute Other Expenses proportionally and update UnitCost for all items
  // Only call this when OtherExpenses actually changes, not on every product update
  const distributeOtherExpensesAndUpdateUnitCost = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      // Use PO data from state, only fetch if not available
      let currentPoData = poData;
      if (!currentPoData) {
        const poResponse = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);
        if (poResponse.data && poResponse.data.data) {
          currentPoData = poResponse.data.data;
        } else {
          return; // Can't proceed without PO data
        }
      }

      const otherExpenses = parseFloat(currentPoData.pO_OtherExpenses ?? currentPoData.otherExpenses ?? currentPoData.OtherExpenses ?? 0);
      
      // Use poItems from state instead of fetching
      if (!poItems || poItems.length === 0) return;

      // Calculate total supplier value from state (sum of OrderQuantity * SupplierPrice)
      const totalSupplierValue = poItems.reduce((sum, item) => {
        const orderQty = parseFloat(item.orderQuantity || 0);
        const supplierPrice = parseFloat(item.supplierPrice || 0);
        return sum + (orderQty * supplierPrice);
      }, 0);

      if (totalSupplierValue === 0) {
        // If no supplier value, set UnitCost = SupplierPrice for all items
        const updatePromises = poItems.map(async (item) => {
          const supplierPrice = parseFloat(item.supplierPrice || 0);
          const currentUnitCost = parseFloat(item.unitCost || 0);
          
          // Only update if UnitCost is different from SupplierPrice
          if (Math.abs(currentUnitCost - supplierPrice) > 0.01) {
            const updatePayload = {
              Id: item.id,
              PO_ID: parseInt(poId),
              MenuItem_ID: item.menuItem_ID,
              CompID: compID,
              MenuItem_Name: item.menuItem_DefaultVariantTitle || item.MenuItem_DefaultVariantTitle || item.menuItem_Name,
              ContainerType: item.containerType || "Individual",
              ContainerQuantity: parseInt(item.containerQuantity || 1),
              MeasuringType: item.MeasuringType || item.menuItem_MeasurementType || "Unit",
              OrderQuantity: parseFloat(item.orderQuantity || 0),
              OrderQuantityInBaseUnit: await calculateBaseUnitQuantity(item),
              Unit: item.unit || "",
              SupplierPrice: supplierPrice,
              UnitCost: supplierPrice // Set UnitCost = SupplierPrice when no expenses
            };

            return axiosInstance.put(APIs.PUT_UPDATE_PO_PRODUCT, updatePayload);
          }
          return Promise.resolve();
        });

        await Promise.all(updatePromises);
        // Refresh items list after updates
        await fetchPurchaseOrderItems();
        return;
      }

      // When other expenses is 0, set UnitCost = SupplierPrice for all items so cost is calculated
      if (otherExpenses <= 0) {
        const updatePromises = poItems.map(async (item) => {
          const supplierPrice = parseFloat(item.supplierPrice || 0);
          const currentUnitCost = parseFloat(item.unitCost || 0);
          if (Math.abs(currentUnitCost - supplierPrice) > 0.01) {
            const updatePayload = {
              Id: item.id,
              PO_ID: parseInt(poId),
              MenuItem_ID: item.menuItem_ID,
              CompID: compID,
              MenuItem_Name: item.menuItem_DefaultVariantTitle || item.MenuItem_DefaultVariantTitle || item.menuItem_Name,
              ContainerType: item.containerType || "Individual",
              ContainerQuantity: parseInt(item.containerQuantity || 1),
              MeasuringType: item.MeasuringType || item.menuItem_MeasurementType || "Unit",
              OrderQuantity: parseFloat(item.orderQuantity || 0),
              OrderQuantityInBaseUnit: await calculateBaseUnitQuantity(item),
              Unit: item.unit || "",
              SupplierPrice: supplierPrice,
              UnitCost: supplierPrice
            };
            return axiosInstance.put(APIs.PUT_UPDATE_PO_PRODUCT, updatePayload);
          }
          return Promise.resolve();
        });
        await Promise.all(updatePromises);
        await fetchPurchaseOrderItems();
        return;
      }

      // Calculate proportional expenses and update all items in parallel
      const updatePromises = poItems.map(async (item) => {
        const orderQty = parseFloat(item.orderQuantity || 0);
        const supplierPrice = parseFloat(item.supplierPrice || 0);
        const itemSupplierValue = orderQty * supplierPrice;
        
        // Calculate proportional share of expenses
        const itemShareOfExpenses = (itemSupplierValue / totalSupplierValue) * otherExpenses;
        const expensePerUnit = orderQty > 0 ? itemShareOfExpenses / orderQty : 0;
        
        // Calculate new UnitCost = SupplierPrice + Expense per Unit
        const newUnitCost = supplierPrice + expensePerUnit;

        const updatePayload = {
          Id: item.id,
          PO_ID: parseInt(poId),
          MenuItem_ID: item.menuItem_ID,
          CompID: compID,
          MenuItem_Name: item.menuItem_DefaultVariantTitle || item.MenuItem_DefaultVariantTitle || item.menuItem_Name,
          ContainerType: item.containerType || "Individual",
          ContainerQuantity: parseInt(item.containerQuantity || 1),
          MeasuringType: item.MeasuringType || item.menuItem_MeasurementType || "Unit",
          OrderQuantity: orderQty,
          OrderQuantityInBaseUnit: await calculateBaseUnitQuantity(item),
          Unit: item.unit || "",
          SupplierPrice: supplierPrice,
          UnitCost: newUnitCost
        };

        return axiosInstance.put(APIs.PUT_UPDATE_PO_PRODUCT, updatePayload);
      });

      // Execute all updates in parallel
      await Promise.all(updatePromises);

      // Refresh items list after updates
      await fetchPurchaseOrderItems();
    } catch (error) {
      console.error("Error distributing other expenses:", error);
      showErrorToast("Failed to update unit costs");
    }
  }, [poId, compID, poData, poItems, calculateBaseUnitQuantity, fetchPurchaseOrderItems]);

  // Update purchase order total amount and quantity
  const updatePurchaseOrderTotal = useCallback(async (totalAmount) => {
    if (!poId || !compID) return;

    try {
      // Use PO data from state, only fetch if not available
      let currentPoData = poData;
      if (!currentPoData) {
        const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);
        if (response.data && response.data.data) {
          currentPoData = response.data.data;
        } else {
          console.error("Could not fetch PO data for update");
          return;
        }
      }

      // Calculate total quantity from state (no API call)
      const totalQuantity = calculateTotalQuantity();

      const updatePayload = {
        PO_ID: parseInt(poId),
        PO_Name: currentPoData.pO_Name || "",
        DeliveryDueDate: currentPoData.deliveryDueDate || new Date().toISOString(),
        PO_DeliveryType: currentPoData.pO_DeliveryType || "Standard",
        SupplierID: currentPoData.supplierID || 0,
        SupplierName: currentPoData.supplierName || "",
        PO_status: currentPoData.pO_status || "Draft",
        PO_Paymentstatus: currentPoData.pO_Paymentstatus || "Unpaid",
        PO_Quantity: totalQuantity.toString(),
        PO_Amount: totalAmount,
        PO_PaidAmount: currentPoData.pO_PaidAmount || 0,
        OtherExpenses: parseFloat(currentPoData.pO_OtherExpenses ?? currentPoData.otherExpenses ?? currentPoData.OtherExpenses ?? 0),
        PO_IsSendEmail: currentPoData.pO_IsSendEmail || false,
        DeliverStockToCompID: currentPoData.deliverStockToCompID || 0,
        StaffUserName: currentUserName || "System",
        CompID: compID,
        CreatedBy: currentPoData.createdBy || currentPoData.CreatedBy || currentUserName || "System",
        ModifiedBy: currentUserName || "System",
        PurchaseOrderPayments: [],
        PurchaseOrderProductLists: []
      };
 
      const response = await axiosInstance.put(APIs.PUT_UPDATE_PURCHASE_ORDER, updatePayload);
   
      // Update local PO data state with new amount instead of re-fetching
      setPoData(prev => prev ? { ...prev, pO_Amount: totalAmount, PO_Amount: totalAmount, pO_Quantity: totalQuantity.toString(), PO_Quantity: totalQuantity.toString() } : prev);
    } catch (error) {
      console.error("Error updating purchase order total:", error);
      console.error("Error details:", error.response?.data || error.message);
      showErrorToast(`Failed to update PO total: ${error.response?.data?.message || error.message}`);
    }
  }, [poId, compID, poData, currentUserName, calculateTotalQuantity]);

  // Save relations on modal submit - add new products
  const handleSaveRelations = async (newSelectedProducts) => {
    try {
      setLoading(true);

      // Use current poItems state to find new ones (avoid unnecessary API call)
      const currentItemIds = poItems.map(item => item.menuItem_ID);
      const productsToAdd = newSelectedProducts.filter(product => !currentItemIds.includes(product.menuItem_ID));

      if (productsToAdd.length === 0) {
        showSuccessToast("All products are already in the purchase order");
        return true;
      }

      // Prepare all product payloads in parallel (fetch missing details if needed)
      const productPayloads = await Promise.all(
        productsToAdd.map(async (product) => {
          // Try to get full product details if container info is missing
          let productData = product;
          if (!product.menuItem_PurchaseContainerType && !product.MenuItem_PurchaseContainerType && !product.containerType) {
            try {
              const productResponse = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
                params: { CompID: compID, MenuItem_ID: product.menuItem_ID }
              });
              if (productResponse.data && productResponse.data.data) {
                productData = { ...product, ...productResponse.data.data };
              }
            } catch (err) {
              console.warn(`Failed to fetch full product details for ${product.menuItem_ID}:`, err);
            }
          }

          const purchaseContainerType = productData.menuItem_PurchaseContainerType || productData.MenuItem_PurchaseContainerType || "Individual";
          const purchaseContainerQty = productData.menuItem_PurchaseContainerQty || productData.MenuItem_PurchaseContainerQty || 1;
          const measuringType = productData.menuItem_MeasurementType || productData.MenuItem_MeasurementType || "Unit";

          // Ensure container quantity is a number
          const containerQtyNum = typeof purchaseContainerQty === 'number'
            ? purchaseContainerQty
            : parseInt(purchaseContainerQty) || 1;

          const productItem = {
            orderQuantity: product.orderQuantity || 1,
            containerType: purchaseContainerType,
            containerQuantity: containerQtyNum,
            unit: product.unit || product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || "",
            menuItem_DefaultSize: product.menuItem_DefaultSize || product.MenuItem_DefaultSize,
            menuItem_DefaultSizeUnit: product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || ""
          };

          const baseUnitQuantity = await calculateBaseUnitQuantity(productItem);

          return {
            PO_ID: parseInt(poId),
            MenuItem_ID: product.menuItem_ID,
            CompID: compID,
            MenuItem_Name: product.menuItem_DefaultVariantTitle || product.MenuItem_DefaultVariantTitle || product.menuItem_Name || product.MenuItem_Name,
            ContainerType: purchaseContainerType,
            ContainerQuantity: containerQtyNum,
            MeasuringType: measuringType,
            OrderQuantity: parseFloat(product.orderQuantity || 1),
            Unit: product.unit || product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || "",
            SupplierPrice: parseFloat(product.supplierPrice || 0),
            OrderQuantityInBaseUnit: baseUnitQuantity,
            UnitCost: product.unitCost ? parseFloat(product.unitCost) : parseFloat(product.supplierPrice || 0),
            MenuItem_PurchaseContainerType: purchaseContainerType,
            MenuItem_PurchaseContainerQty: containerQtyNum,
            MenuItem_DefaultSize: product.menuItem_DefaultSize || product.MenuItem_DefaultSize,
            MenuItem_DefaultSizeUnit: product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || ""
          };
        })
      );

      // Batch POST all new products in parallel
      await Promise.all(
        productPayloads.map(payload => axiosInstance.post(APIs.POST_ADD_PRODUCT_TO_PO, payload))
      );

      showSuccessToast("Products added successfully");

      // Transform and update local state immediately
      const transformedNewProducts = productsToAdd.map((product, index) => {
        const payload = productPayloads[index];
        return {
          id: null, // Will be set by database after refresh
          menuItem_ID: product.menuItem_ID,
          menuItem_Name: product.menuItem_DefaultVariantTitle || product.MenuItem_DefaultVariantTitle || product.menuItem_Name || product.MenuItem_Name,
          menuItem_DefaultVariantTitle: product.menuItem_DefaultVariantTitle || product.MenuItem_DefaultVariantTitle,
          menuItem_Image: product.menuItem_Image,
          orderQuantity: product.orderQuantity?.toString() || "1",
          unit: product.unit || product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || "",
          supplierPrice: product.supplierPrice?.toString() || "0",
          unitCost: product.unitCost?.toString() || product.supplierPrice?.toString() || "0",
          containerType: payload.ContainerType || "Individual",
          containerQuantity: payload.ContainerQuantity?.toString() || "1",
          menuItem_PurchaseContainerType: payload.MenuItem_PurchaseContainerType || "Individual",
          menuItem_PurchaseContainerQty: payload.MenuItem_PurchaseContainerQty || 1,
          menuItem_MeasurementType: payload.MeasuringType || "Unit",
          menuItem_DefaultSize: product.menuItem_DefaultSize || product.MenuItem_DefaultSize,
          menuItem_DefaultSizeUnit: product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit,
          MeasuringType: payload.MeasuringType || "Unit",
          totalSupplierPrice: "0",
          totalCost: "0",
          current_Quantity: product.current_Quantity ?? product.Current_Quantity ?? 0,
        };
      });

      // Update local state immediately
      setPoItems([...poItems, ...transformedNewProducts]);

      // Refresh from database to get correct IDs and ensure data consistency
      await fetchPurchaseOrderItems();

      // Distribute other expenses and update UnitCost
      await distributeOtherExpensesAndUpdateUnitCost();

      // Calculate and update total supplier price (now uses state, no API call)
      const totalAmount = calculateTotalSupplierPrice();
      console.log("Calculated total amount after adding products:", totalAmount);
      if (totalAmount >= 0) {
        await updatePurchaseOrderTotal(totalAmount);
      }
      
      return true;
    } catch (error) {
      console.error("Error saving relations:", error);
      showErrorToast("Failed to add products");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Memoized callback for product updates
  const handleProductUpdated = useCallback(async (updatedProducts) => {
    // Update local state immediately with the updated products to show changes right away
    setPoItems(updatedProducts);
    
    // Always run cost calculation: when otherExpenses is 0 it sets UnitCost = SupplierPrice; when > 0 it distributes proportionally
    await distributeOtherExpensesAndUpdateUnitCost();
    
    // Calculate and update PO total after product update/delete
    // Use updatedProducts for calculation to get immediate feedback
    const totalAmount = updatedProducts.reduce((sum, item) => {
      const orderQty = parseFloat(item.orderQuantity || 0);
      const supplierPrice = parseFloat(item.supplierPrice || 0);
      return sum + (orderQty * supplierPrice);
    }, 0);
    
    console.log("Calculated total amount after update/delete:", totalAmount);
    if (totalAmount >= 0) { // Allow 0 for empty PO
      await updatePurchaseOrderTotal(totalAmount);
    }
  }, [poData, distributeOtherExpensesAndUpdateUnitCost, updatePurchaseOrderTotal, fetchPurchaseOrderItems]);

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      // Only load essential data on mount - products will be loaded lazily when modal opens
      await Promise.all([
        fetchPurchaseOrder(),
        fetchPurchaseOrderItems()
      ]);
      setInitialLoading(false);
    };
    loadData();
  }, [fetchPurchaseOrder, fetchPurchaseOrderItems]);

  // Load products when Add Products modal opens (each branch has his own PO, so branch stock is used when user is branch)
  useEffect(() => {
    if (showAddProductsModal) {
      fetchAllProducts();
    }
  }, [showAddProductsModal, fetchAllProducts]);

  // Distribute expenses when PO OtherExpenses changes (trigger directly, no delay)
  useEffect(() => {
    // Only trigger when OtherExpenses actually changes and we have items
    if (poData && poItems.length > 0) {
      const otherExpenses = parseFloat(poData.pO_OtherExpenses ?? poData.otherExpenses ?? poData.OtherExpenses ?? 0);
      // Only distribute if there are expenses or if we need to reset unit costs
      if (otherExpenses !== undefined && !isNaN(otherExpenses)) {
        distributeOtherExpensesAndUpdateUnitCost();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData?.pO_OtherExpenses, poData?.otherExpenses, poData?.OtherExpenses]);

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/purchaseOrder/managePurchaseOrders">Purchase Orders</Link>
          </li>
          <li className="breadcrumb-item active">Purchase Order Items</li>
        </ol>
        <h1 className="page-header mb-0">
          {poData ? `PO-${poData.pO_ID}: ${poData.pO_Name} - Items` : "Purchase Order Items"}
        </h1>
      </div>
      <div className="ms-auto d-flex gap-2 align-items-center">
        <button
          onClick={() => setShowAddProductsModal(true)}
          className="btn px-4"
          style={{ 
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
          }}
        >
          <i className="fa-solid fa-plus" style={{ fontSize: "12px" }}></i>
          Add Products
        </button>

        <button
          onClick={async () => {
            if (!poItems || poItems.length === 0) {
              showErrorToast("No products to calculate costs for");
              return;
            }
            
            const confirm = await swal({
              title: "Calculate Cost Prices?",
              text: "This will recalculate and update UnitCost and TotalCost for all products based on Supplier Price and Other Expenses. Continue?",
              icon: "info",
              buttons: true,
              dangerMode: false,
            });
            
            if (confirm) {
              try {
                setLoading(true);
                await distributeOtherExpensesAndUpdateUnitCost();
                
                // Calculate and update PO total after cost calculation
                const totalAmount = calculateTotalSupplierPrice();
                if (totalAmount >= 0) {
                  await updatePurchaseOrderTotal(totalAmount);
                }
                
                showSuccessToast("Cost prices calculated and updated successfully for all products");
              } catch (error) {
                console.error("Error calculating costs:", error);
                showErrorToast("Failed to calculate cost prices");
              } finally {
                setLoading(false);
              }
            }
          }}
          className="btn px-4"
          style={{ 
            backgroundColor: "#28a745",
            background: `linear-gradient(135deg, #28a745 0%, #20c997 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px #28a74540`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = `linear-gradient(135deg, #20c997 0%, #28a745 100%)`;
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = `0 4px 12px #28a74560`;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, #28a745 0%, #20c997 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = `0 2px 8px #28a74540`;
          }}
          disabled={loading || !poItems || poItems.length === 0}
        >
          <i className="fa-solid fa-calculator" style={{ fontSize: "12px" }}></i>
          Calculate Costs
        </button>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}
      {poId && <PurchaseOrderTabBar poId={poId} />}

      <Panel className="mb-4">
        <PanelHeader
          style={{
            backgroundColor: themeColor,
            color: 'white',
            border: 'none',
            borderRadius: '10px 10px 0 0',
            padding: '15px 20px',
            fontWeight: '600',
            fontSize: '16px'
          }}
        >
          <i className="fa fa-box me-2"></i>
          Purchase Order Items Management
        </PanelHeader>
        <PanelBody
          style={{
            padding: '25px',
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            borderRadius: '0 0 10px 10px',
            border: `1px solid ${themeColor}20`
          }}
        >
          <PurchaseOrderProductSelector
            selectedProducts={poItems}
            setSelectedProducts={handleProductSelectionChange}
            setLoading={setLoading}
            compID={compID}
            poId={poId}
            onSaveRelations={handleSaveRelations}
            onProductUpdated={handleProductUpdated}
            products={allProducts}
            showAddProductsModal={showAddProductsModal}
            setShowAddProductsModal={setShowAddProductsModal}
            stockLabel={parentCompId && parseInt(parentCompId, 10) > 0 ? "Branch stock" : undefined}
          />
        </PanelBody>
      </Panel>
    </div>
  );
};

export default UpdatePurchaseOrderItems;